import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useQueryClient } from '@tanstack/react-query';

const RestaurantContext = createContext(null);

export function RestaurantProvider({ children }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentRestaurantId, setCurrentRestaurantId] = useState(null);

  // Initialize from localStorage or user data
  useEffect(() => {
    if (!user) return;

    const stored = localStorage.getItem('current_restaurant_id');
    const restaurants = user.restaurants || [];

    let selectedId = null;
    if (user.role === 'org_admin' && restaurants.length > 0) {
      // Org admin: use stored preference or first restaurant
      const valid = restaurants.find(r => r.id === Number(stored));
      selectedId = valid ? valid.id : restaurants[0].id;
    } else if (user.restaurant) {
      // Restaurant admin / staff: locked to their restaurant
      selectedId = user.restaurant;
    } else if (restaurants.length > 0) {
      selectedId = restaurants[0].id;
    }
    if (selectedId) {
      setCurrentRestaurantId(selectedId);
      localStorage.setItem('current_restaurant_id', String(selectedId));
    }
  }, [user]);

  const switchRestaurant = useCallback((id) => {
    setCurrentRestaurantId(id);
    localStorage.setItem('current_restaurant_id', String(id));
    // Invalidate all queries so data refreshes for new restaurant
    queryClient.invalidateQueries();
  }, [queryClient]);

  const currentRestaurant = user?.restaurants?.find(r => r.id === currentRestaurantId) || null;
  const isOrgAdmin = user?.role === 'org_admin';
  const restaurants = user?.restaurants || [];

  return (
    <RestaurantContext.Provider value={{
      currentRestaurantId,
      currentRestaurant,
      restaurants,
      switchRestaurant,
      isOrgAdmin,
    }}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const ctx = useContext(RestaurantContext);
  if (!ctx) throw new Error('useRestaurant must be used within RestaurantProvider');
  return ctx;
}
